import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Plus, Pencil, Trash2, X, Save, Eye, Bold, Italic, Link, Heading, List, Code, Type } from 'lucide-react';

const extractBody = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  div.querySelectorAll("style, script, head, meta, link, title").forEach(el => el.remove());
  const body = div.querySelector("body");
  return body ? body.innerHTML : div.innerHTML;
};

const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (editorRef.current && !init) {
      editorRef.current.innerHTML = value || '';
      setInit(true);
    }
  }, [value, init]);

  const execCmd = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (!url) return;
    const text = prompt('Link text (kosongkan untuk pakai URL):') || url;
    document.execCommand('insertHTML', false, `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#f59e0b;text-decoration:underline">${text}</a>`);
    onChange(editorRef.current.innerHTML);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('bold')}><Bold size={14} /> Bold</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('italic')}><Italic size={14} /> Italic</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('underline')}>U&#818;</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('formatBlock', '<h2>')}><Heading size={14} /> H2</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('formatBlock', '<h3>')}>H3</button>
        <button type="button" className="toolbar-btn" onClick={insertLink}><Link size={14} /> Anchor</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('insertUnorderedList')}><List size={14} /></button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('formatBlock', '<blockquote>')}>Quote</button>
        <button type="button" className="toolbar-btn" onClick={() => execCmd('formatBlock', '<p>')}>Paragraph</button>
        <button type="button" className="toolbar-btn" onClick={() => { execCmd('removeFormat'); execCmd('formatBlock', '<p>'); }}>Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={() => onChange(editorRef.current.innerHTML)}
        style={{
          minHeight: 350, padding: 20, fontSize: 15, lineHeight: 1.9,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 8, color: '#ddd', outline: 'none', overflow: 'auto', maxHeight: 600
        }}
      />
    </div>
  );
};

const HTMLEditor = ({ value, onChange }) => {
  const [code, setCode] = useState(value || '');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { setCode(value || ''); }, [value]);

  const handleChange = (e) => {
    setCode(e.target.value);
    const clean = extractBody(e.target.value);
    onChange(clean);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button type="button" className={`toolbar-btn ${!showPreview ? 'active' : ''}`} onClick={() => setShowPreview(false)}>
          <Code size={14} /> HTML Code
        </button>
        <button type="button" className={`toolbar-btn ${showPreview ? 'active' : ''}`} onClick={() => setShowPreview(true)}>
          <Eye size={14} /> Preview
        </button>
      </div>
      {!showPreview ? (
        <textarea
          value={code}
          onChange={handleChange}
          placeholder={'Paste atau ketik HTML di sini...\n\nContoh:\n<h2>Judul Bab</h2>\n<p>Isi cerita paragraf pertama...</p>\n<p>Paragraf kedua dengan <a href="https://example.com">link</a>...</p>\n<blockquote>Kutipan dialog...</blockquote>'}
          style={{
            width: '100%', minHeight: 350, padding: 16, fontSize: 13, lineHeight: 1.6,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 8, color: '#4caf50', outline: 'none', resize: 'vertical',
            fontFamily: "'Courier New', monospace', monospace", maxHeight: 600
          }}
        />
      ) : (
        <div style={{
          minHeight: 350, padding: 20, fontSize: 15, lineHeight: 1.9,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 8, color: '#ddd', overflow: 'auto', maxHeight: 600
        }}
          dangerouslySetInnerHTML={{ __html: extractBody(code) }}
        />
      )}
    </div>
  );
};

const StoryManager = () => {
  const { api } = useAuth();
  const [stories, setStories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: '', thumbnail: '', author: 'Admin', tags: '' });
  const [editorMode, setEditorMode] = useState('rich');
  const [storyCategories, setStoryCategories] = useState([]);
  useEffect(() => {
    api.get('/admin/nav').then(r => {
      const ceritaNav = (r.data || []).find(n => n.link_type === 'cerita' || n.label === 'Cerita Dewasa');
      if (ceritaNav && ceritaNav.children && ceritaNav.children.length > 0) {
        setStoryCategories(ceritaNav.children.map(ch => ch.label || ch.link_category));
      }
    }).catch(() => {});
  }, []);

  const load = () => api.get('/admin/stories').then(r => setStories(r.data || [])).catch(console.error);
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', content: '', category: '', thumbnail: '', author: 'Admin', tags: '' }); setEditing(null); setShowForm(false); setEditorMode('rich'); };

  const handleThumbUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/admin/upload/banner', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, thumbnail: res.data.url }));
    } catch (err) { alert('Upload failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags };
    try {
      if (editing) { await api.put(`/admin/stories/${editing}`, payload); }
      else { await api.post('/admin/stories', payload); }
      resetForm(); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (s) => {
    setForm({ title: s.title, content: s.content, category: s.category || '', thumbnail: s.thumbnail || '', author: s.author || 'Admin', tags: (s.tags || []).join(', ') });
    setEditing(s.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this story?')) return;
    await api.delete(`/admin/stories/${id}`); load();
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><BookOpen size={20} /> Cerita Dewasa ({stories.length})</h2>
        <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} /> Add Story</button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>{editing ? 'Edit Story' : 'Add New Story'}</h3>
            <button className="btn-sm btn-delete" onClick={resetForm}><X size={14} /></button>
          </div>
          <form onSubmit={handleSave}>
            <div className="two-col">
              <div>
                <label className="form-label">Title *</label>
                <input className="form-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="Judul cerita..." />
              </div>
              <div>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="">Select Category</option>
                  {storyCategories.map((cat, i) => (
                    <option key={i} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Author</label>
                <input className="form-input" value={form.author} onChange={e => setForm({...form, author: e.target.value})} placeholder="Author name" />
              </div>
              <div>
                <label className="form-label">Tags (comma separated)</label>
                <input className="form-input" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="hot, viral, romance" />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="form-label">Thumbnail</label>
              <div style={{fontSize:10,color:'#f59e0b',marginBottom:4,padding:'6px 10px',background:'rgba(245,158,11,0.06)',borderRadius:6,lineHeight:1.6}}>
                🖼️ <strong>Ukuran:</strong> 640×360px (16:9) &nbsp;|&nbsp; <strong>Format:</strong> JPG, PNG, WebP &nbsp;|&nbsp; <strong>Max:</strong> 2MB
              </div>
              <input type="file" accept="image/*" onChange={handleThumbUpload} className="form-input" />
              {form.thumbnail && <img src={form.thumbnail} alt="thumb" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6, marginTop: 6 }} />}
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="form-label">Content</label>
              <div className="admin-tabs" style={{ marginBottom: 0 }}>
                <button type="button" className={`admin-tab ${editorMode === 'rich' ? 'active' : ''}`} onClick={() => setEditorMode('rich')}>
                  <Type size={14} style={{ marginRight: 4 }} /> Rich Editor
                </button>
                <button type="button" className={`admin-tab ${editorMode === 'html' ? 'active' : ''}`} onClick={() => setEditorMode('html')}>
                  <Code size={14} style={{ marginRight: 4 }} /> HTML Editor
                </button>
              </div>
              {editorMode === 'rich' ? (
                <RichEditor value={form.content} onChange={(html) => setForm({...form, content: html})} />
              ) : (
                <HTMLEditor value={form.content} onChange={(html) => setForm({...form, content: html})} />
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn-primary"><Save size={16} /> {editing ? 'Update' : 'Publish Story'}</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr><th>Thumb</th><th>Title</th><th>Author</th><th>Category</th><th>Views</th><th>Date</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {stories.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#555' }}>No stories yet. Click "Add Story" to write one!</td></tr>
            ) : stories.map(s => (
              <tr key={s.id}>
                <td>{s.thumbnail ? <img src={s.thumbnail} alt="" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '📖'}</td>
                <td style={{ fontWeight: 600, color: '#ddd', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</td>
                <td style={{ color: '#f59e0b' }}>{s.author}</td>
                <td><span className="badge badge-info">{s.category || '-'}</span></td>
                <td><Eye size={12} style={{ marginRight: 4 }} />{s.views || 0}</td>
                <td style={{ fontSize: 11, color: '#555' }}>{new Date(s.created_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-sm btn-edit" onClick={() => handleEdit(s)}><Pencil size={12} /></button>
                    <button className="btn-sm btn-delete" onClick={() => handleDelete(s.id)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoryManager;
